import { Box, Checkbox, Chip, FormControlLabel, Grid, FormHelperText, Typography } from "@mui/material"
import { FieldControl } from "../../../../shared/components"
import dayjs from "dayjs";
import { useDisplayField } from "../../../../hooks/useDisplayField";

export const Schedule = ({ meetingData, onChange, errors = {}, isRecurring }: { meetingData: any, onChange: (key: string, value: any) => void, errors?: { [key: string]: string }, isRecurring: boolean }) => {

    const { getFilterOptions } = useDisplayField([]);

    // Check if the meeting date is today
    const isToday = meetingData.meeting_from_date && dayjs(meetingData.meeting_from_date).isSame(dayjs(), 'day');
    
    // Get current time for minTime on start_time when date is today
    const minStartTime = isToday ? dayjs().format("HH:mm:ss") : undefined;

    const weekdays = [
        { label: 'M', value: 'Monday', key: 1 },
        { label: 'T', value: 'Tuesday', key: 2 },
        { label: 'W', value: 'Wednesday', key: 3 },
        { label: 'T', value: 'Thursday', key: 4 },
        { label: 'F', value: 'Friday', key: 5 },
        { label: 'S', value: 'Saturday', key: 6 },
        { label: 'S', value: 'Sunday', key: 7 },
    ];

    const toggleWeekday = (day: number) => {
        const selectedDays = meetingData.recurring_weekdays || [];
        if (selectedDays.includes(day)) {
            onChange('recurring_weekdays',
                selectedDays.filter((d: number) => d !== day)
            );
            return;
        } else {
            onChange('recurring_weekdays', [...selectedDays, day]);
        }
    };

    const getMonthlyDescription = () => {
        if (!meetingData.meeting_from_date) return '';
        const date = dayjs(meetingData.meeting_from_date);
        const dayOfMonth = date.date();
        const dayName = date.format('dddd');
        const weekOfMonth = Math.ceil(dayOfMonth / 7);
        const ordinal = ['first', 'second', 'third', 'fourth', 'fifth'][weekOfMonth - 1] || weekOfMonth;
        return meetingData.monthly_recurring_option === 'day_of_month'
            ? `On day ${dayOfMonth} of every month`
            : `On the ${ordinal} ${dayName} of every month`;
    };

    return (
        <Grid container spacing={2} alignItems={'center'}>

            <Grid size={12}>
                <FieldControl
                    keyName={"meeting_label"}
                    label={"Meeting Title"}
                    variant={"input"}
                    onChange={onChange}
                    value={meetingData.meeting_label}
                    error={!!errors.meeting_label}
                    helperText={errors.meeting_label}
                    required
                />
            </Grid>

            <Grid size={meetingData.is_recurring ? 3 : 4}>
                <FieldControl
                    keyName={"meeting_from_date"}
                    label={"Meeting From Date"}
                    variant={"date"}
                    onChange={onChange}
                    value={meetingData.meeting_from_date}
                    error={!!errors.meeting_from_date}
                    helperText={errors.meeting_from_date}
                    required
                />
            </Grid>

            <Grid size={meetingData.is_recurring ? 3 : 4}>
                <FieldControl
                    keyName={"start_time"}
                    label={"Start Time"}
                    variant={"time"}
                    onChange={onChange}
                    value={meetingData.start_time}
                    error={!!errors.start_time}
                    helperText={errors.start_time}
                    minTime={minStartTime}
                    required
                />
            </Grid>

            <Grid size={meetingData.is_recurring ? 3 : 4}>
                <FieldControl
                    keyName={"end_time"}
                    label={"End Time"}
                    variant={"time"}
                    onChange={onChange}
                    value={meetingData.end_time}
                    error={!!errors.end_time}
                    helperText={errors.end_time}
                    minTime={meetingData.start_time}
                    required
                />
            </Grid>

            {
                meetingData.is_recurring && (
                    <Grid size={3}>
                        <FieldControl
                            keyName={"meeting_to_date"}
                            label={"End Date"}
                            variant={"date"}
                            onChange={onChange}
                            value={meetingData.meeting_to_date}
                            minDate={meetingData.meeting_from_date}
                        />
                    </Grid>
                )
            }

            <Grid size={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={meetingData.is_recurring}
                            onChange={e => onChange("is_recurring", e.target.checked)}
                            disabled={isRecurring}
                        />
                    }
                    label="Recurring Meeting"
                />
            </Grid>

            {
                meetingData.is_recurring && !isRecurring && (
                    <>
                        <Grid size={6}>
                            <FieldControl
                                keyName={"recurring_type"}
                                label={"Recurring Type"}
                                variant={"select"}
                                onChange={onChange}
                                value={meetingData.recurring_type}
                                options={getFilterOptions("MEETING_FREQUENCY").map(opt => ({ value: opt.key, label: opt.display_text }))}
                                disabled={isRecurring}
                                required
                            />
                        </Grid>
                        <Grid size={6}>
                            <FieldControl
                                keyName={"recurring_count"}
                                label={"Occurrences ( Repeat every )"}
                                variant={"outlined"}
                                onChange={onChange}
                                value={meetingData.recurring_count}
                                disabled={isRecurring}
                                required
                            />
                        </Grid>

                        {meetingData.recurring_type === 'weekly' && (
                            <Grid size={12}>
                                <Box display={'flex'} flexDirection={'column'} gap={1}>
                                    <FormHelperText sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                                        Select days of the week
                                    </FormHelperText>
                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                        {weekdays.map((day) => (
                                            <Chip
                                                key={day.key}
                                                label={day.label}
                                                onClick={() => toggleWeekday(day.key)}
                                                color={(meetingData.recurring_weekdays || []).includes(day.key) ? 'primary' : 'default'}
                                                variant={(meetingData.recurring_weekdays || []).includes(day.key) ? 'filled' : 'outlined'}
                                                clickable
                                                disabled={isRecurring}
                                            />
                                        ))}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        happens on every {(meetingData.recurring_weekdays || []).length > 0 ? ` ${weekdays.filter(d => (meetingData.recurring_weekdays || []).includes(d.key)).map(d => d.value).join(', ')}` : ' ...no days selected'}
                                    </Typography>
                                    {errors.recurring_weekdays && (
                                        <FormHelperText error>{errors.recurring_weekdays}</FormHelperText>
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {meetingData.recurring_type === 'monthly' && (
                            <Grid size={12}>
                                <FieldControl
                                    keyName={"monthly_recurring_option"}
                                    label={"Monthly Pattern"}
                                    variant={"radio"}
                                    onChange={onChange}
                                    value={meetingData.monthly_recurring_option}
                                    options={getFilterOptions('MONTHLY_RECURRENCE_PATTERN').map((opt: any) => ({
                                        value: opt.value,
                                        label: opt.display_text
                                    }))}
                                    required
                                />
                                {meetingData.meeting_from_date && (
                                    <FormHelperText sx={{ color: 'text.secondary', ml: 1 }}>
                                        {getMonthlyDescription()}
                                    </FormHelperText>
                                )}
                            </Grid>
                        )}
                    </>
                )
            }

            <Grid size={12}>
                <FieldControl
                    keyName={"meeting_location"}
                    label={"Meeting Location"}
                    variant={"outlined"}
                    onChange={onChange}
                    value={meetingData.meeting_location}
                    error={!!errors.meeting_location}
                    helperText={errors.meeting_location}
                    required
                />
            </Grid>

        </Grid>
    )
}